import { cloneDeep } from "lodash";
import { insightDefaultStaticRanges } from "../insight/utils";
import { addDays, endOfMonth, startOfMonth } from "date-fns";
import { IVDMPICContent, IVDMPICDepartment, IVDMPICPerson, TDepartmentID, TDepartmentWiseAssignment } from "./@types";

export const staticRanges = cloneDeep(insightDefaultStaticRanges);

const getMonthlyRange = (date?: Date) => {
	if(!date) date = new Date();
	let som = startOfMonth(date);
	let eom = endOfMonth(date);
	const formatSuffix = "T00:00:00.000Z";

	return {
		start: addDays(som, 1).toISOString()?.split("T")[0] + formatSuffix,
		end: eom.toISOString()?.split("T")[0] + formatSuffix,
	};
};

// This is temproary as there is no date range specific API for CNR.
const calculatePagination = (date?: Date): { limit: number; skip: number } => {
    const now = new Date();
    date = startOfMonth(date ?? now);

    // Calculate the number of days between the given date and today (inclusive)
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.ceil(diffInMs / msPerDay) + 1; // +1 to include the start date

    const limit = diffInDays > 0 ? diffInDays : 1; // Ensure at least 1 day
    const skip: number = 0;

    return { limit, skip };
};

const toUTCMidnight = (date: Date): Date => {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
};

const toLocalMidnight = (date: string | Date): Date => {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// wayship, EPS
const vdmShouldBeVisibleToTenants = [
	"8662f830-862f-11eb-929b-e74cc7276690",
	"434d9b50-5703-11eb-8d4a-6b516664bdbd",
];

const getIsVDMVisible = (tenantId?:string) => {
	return vdmShouldBeVisibleToTenants.includes(
		tenantId ?? "",
	);
}

// PIC related Utils

function removeProfilePictures(
	departments: IVDMPICContent["departments"],
): IVDMPICContent["departments"] {
	const sanitizedDepartments = cloneDeep(departments);

	const roles: (keyof IVDMPICDepartment)[] = ["manager", "superintendent", "executive"];

	for (const deptKey of Object.keys(sanitizedDepartments) as (keyof typeof sanitizedDepartments)[]) {
		const department = sanitizedDepartments[deptKey];

		for (const role of roles) {
			const person = department[role]; 
			if (person && typeof person === "object" && "profile_picture" in person) {
				delete (person as Partial<IVDMPICPerson>).profile_picture;
			}
		}
	}

	return sanitizedDepartments;
}

const createEmptyDepartment = (): IVDMPICDepartment => ({
	manager: {},
	superintendent: {},
	executive: {},
});

const createEmptyAssignment = (): TDepartmentWiseAssignment => ({
	technical: createEmptyDepartment(),
	operations_commercial: createEmptyDepartment(),
	marine_hr: createEmptyDepartment(),
	sqv_inspections: createEmptyDepartment(),
});

const isPerson = (
	value: IVDMPICPerson | {} | undefined,
): value is IVDMPICPerson => !!value && "id" in value;

// Runs fn over every department/position slot, returns a new assignment object.
// The purpose is to strip profile_picture(Because during save we are not tracking profile_picture)
function mapAssignment(
	assignment: TDepartmentWiseAssignment,
	fn: (
		person: IVDMPICPerson | {},
		departmentId: TDepartmentID,
		positionId: string,
	) => IVDMPICPerson | {},
): TDepartmentWiseAssignment {
	const result = {} as TDepartmentWiseAssignment;
	(Object.keys(assignment) as TDepartmentID[]).forEach((deptId) => {
		result[deptId] = {} as IVDMPICDepartment;
		(Object.keys(assignment[deptId]) as Array<keyof IVDMPICDepartment>).forEach(
			(posId) => {
				result[deptId][posId] = fn(
					assignment[deptId][posId],
					deptId,
					posId as string,
				);
			},
		);
	});
	return result;
}

// Strips profile_picture before comparing - picture is not tracked as part
// of the draft; this just guards against stale legacy data on either side.
function withoutProfilePicture(assignment: TDepartmentWiseAssignment) {
	return mapAssignment(assignment, (person) =>
		isPerson(person) ? { ...person, profile_picture: "" } : person,
	);
}

const isSlotPersisted = (
	baseline: TDepartmentWiseAssignment,
	departmentId: TDepartmentID,
	positionId: string,
	localPerson: IVDMPICPerson | {},
): boolean => {
	const baselinePerson = (baseline[departmentId] as any)?.[positionId];
	if (!isPerson(localPerson) || !isPerson(baselinePerson)) return false;
	return localPerson.id === baselinePerson.id;
};

// End of PIC related Utils 

export const vdmDashboardUtils = {
	getMonthlyRange,
	calculatePagination,
	toUTCMidnight,
	toLocalMidnight,
	vdmShouldBeVisibleToTenants,
	getIsVDMVisible,
	removeProfilePictures,
	createEmptyAssignment,
	withoutProfilePicture,
	isSlotPersisted,
	isPerson

};
